import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase/firebaseConfig";
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiMoon, FiSun, FiEye, FiEyeOff } from "react-icons/fi";

const Login = () => {
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) navigate("/landing");
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setError("Please enter a valid email address.");
      setIsLoading(false);
      setTimeout(() => setError(""), 4000);
      return;
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      localStorage.setItem("user", JSON.stringify(userCredential.user));
      navigate("/landing");
    } catch {
      setError("Invalid email or password.");
      setTimeout(() => setError(""), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      localStorage.setItem("user", JSON.stringify(result.user));
      navigate("/landing");
    } catch {
      setError("Google login failed. Try again.");
      setTimeout(() => setError(""), 4000);
    }
  };

  const handleGuestMode = () => {
    localStorage.setItem("user", JSON.stringify({ displayName: "Guest User" }));
    navigate("/landing");
  };

  return (
    <div className={`min-h-screen flex flex-col font-uber ${darkMode ? "bg-black text-white" : "bg-white text-black"}`}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Uber+Move:wght@400;700&display=swap');
          .font-uber { font-family: 'Uber Move', 'Inter', sans-serif; }
        `}
      </style>

      <header className="flex justify-between items-center px-6 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}><FiArrowLeft size={22} /></button>
          <img src="/zoomo-logo.png" className="w-8" alt="Zoomo Logo" />
          <h1 className="text-2xl font-extrabold tracking-tight uppercase">
            ZOOMO <span className="text-[#00b86b]">EATS</span>
          </h1>
        </div>
        <motion.button
          onClick={toggleDarkMode}
          className="p-2 rounded-full bg-[#00b86b] hover:bg-[#009e5c] transition"
        >
          {darkMode ? <FiSun className="text-black" size={20} /> : <FiMoon className="text-black" size={20} />}
        </motion.button>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className={`w-full max-w-md rounded-2xl shadow-2xl p-8 ${darkMode ? "bg-[#111]" : "bg-[#fafafa]"}`}>
          <div className="text-center mb-6">
            <motion.img
              src="/zoomo-mascot.png"
              className="w-24 mx-auto mb-6"
              alt="Mascot"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <h1 className="text-2xl font-bold">Welcome Back</h1>
            <p className="text-sm text-gray-500 mt-1">
              Sign in to your <span className="text-[#00b86b] font-semibold">Zoomo Eats</span> account
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#e6f0ff] text-black placeholder:text-gray-500 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b]"
              required
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#e6f0ff] text-black placeholder:text-gray-500 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00b86b] pr-12"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>

            <div className="flex justify-between items-center text-sm text-gray-400">
              <label className="flex items-center gap-2">
                <input type="checkbox" className="accent-[#00b86b]" />
                Remember me
              </label>
              <button
                type="button"
                onClick={() => alert("Forgot password flow coming soon!")}
                className="text-[#00b86b] hover:underline"
              >
                Forgot password?
              </button>
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full py-3 rounded-full bg-[#00b86b] text-black font-bold text-sm ${
                isLoading ? "opacity-60 cursor-not-allowed" : "hover:bg-[#009e5c]"
              }`}
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </motion.button>
          </form>

          <AnimatePresence>
            {error && (
              <motion.div
                className="mt-4 flex items-center gap-2 text-sm text-red-700 font-medium bg-red-100 border border-red-400 px-4 py-2 rounded-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 fill-red-500" viewBox="0 0 20 20">
                  <path d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-8.75-4a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0V6Zm-.75 8.25a.875.875 0 1 0 0-1.75.875.875 0 0 0 0 1.75Z"/>
                </svg>
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="my-6 flex items-center gap-2 text-gray-400 text-sm">
            <hr className="flex-grow border-gray-700" />
            OR
            <hr className="flex-grow border-gray-700" />
          </div>

          <div className="space-y-3">
            <motion.button
              onClick={handleGoogleSignIn}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 rounded-full border border-gray-400 bg-white text-black font-semibold flex items-center justify-center gap-2"
            >
              <img src="https://www.svgrepo.com/show/355037/google.svg" className="w-5 h-5" alt="Google" />
              Continue with Google
            </motion.button>

            <motion.button
              onClick={() => navigate("/phone-login")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 rounded-full bg-[#00b86b] text-black font-bold text-sm hover:bg-[#009e5c]"
            >
              Continue with Phone
            </motion.button>

            <motion.button
              onClick={handleGuestMode}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-3 rounded-full border border-[#00b86b] text-[#00b86b] font-bold text-sm hover:bg-[#00b86b]/10"
            >
              Continue as Guest
            </motion.button>
          </div>

          <p className="text-center mt-6 text-sm text-gray-400">
            Don’t have an account?{" "}
            <button onClick={() => navigate("/signup")} className="text-[#00b86b] hover:underline">
              Create one
            </button>
          </p>
        </div>
      </main>

      <footer className="text-center text-sm text-gray-500 py-4 font-medium">
        © {new Date().getFullYear()} <span className="font-semibold text-[#00b86b]">Zoomo Eats</span> — Designed with <span className="text-red-500">❤️</span> by Shiv
      </footer>
    </div>
  );
};

export default Login;
